import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import authService from '../services/authService';
import orgService from '../services/orgService';
import fcmService from '../services/fcmService';
import { LoginCredentials } from '../types';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validate form
      if (!formData.email.trim() || !formData.password.trim()) {
        setError('Please enter both email and password.');
        setIsLoading(false);
        return;
      }

      // Call login API
      const response = await authService.login({
        email: formData.email.trim(),
        password: formData.password
      });

      console.log('Login successful:', response);
      
      // Fetch and store organization_id after successful login
      try {
        await orgService.fetchUserOrganization();
        console.log('Organization ID fetched and stored successfully');
      } catch (orgError) {
        // Log error but don't block navigation - organization_id can be fetched later
        console.error('Failed to fetch organization ID:', orgError);
        // Optionally show a warning to user, but allow them to continue
      }

      // Initialize FCM only for admin users after successful login.
      // Do not await: run in background so login never gets stuck if the browser
      // does not show the notification permission prompt (e.g. Microsoft Edge).
      if (fcmService.shouldInitialize()) {
        fcmService.initializeAndRegister()
          .then((token) => {
            if (token) console.log('FCM initialized and registered after login');
          })
          .catch((fcmError) => {
            console.error('Failed to initialize FCM after login:', fcmError);
          });
      } else {
        console.log('FCM initialization skipped: User is not an admin or browser does not support FCM');
      }

      // Navigate to dashboard immediately so login is never blocked by FCM/permission prompt
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Login error:', error);
      setError((error as Error).message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header */}
        <div className="login-header">
          <div className="logo-section">
            <div className="royal-cyber-logo">
              <img 
                src="/Royal-Cyber-Logo-2%201.svg" 
                alt="Royal Cyber Logo" 
                width="119" 
                height="29"
              />
            </div>
            <div className="separator"></div>
            <div className="scm-text">SCM</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="login-content">
          <h1 className="login-title">Login</h1>
          
          {/* Error Message */}
          {error && (
            <div className="error-message-container">
              <div className="error-message">{error}</div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-input password-input"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>

            {/* Forgot Password Link */}
            {/* <button type="button" className="forgot-password-link">
              Forgot Password?
            </button> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
